import TileLayer from 'ol/layer/Tile';
import { fromLonLat, toLonLat } from 'ol/proj';
import OlMap from 'ol/Map';

import { MapEntity } from '../../types/game-document/entities';
import { createOSMLayer} from './osm.provider';
import { createAzureLayer} from './azure.provider';
import { createGoogleLayer} from './google.provider';

export interface IMapAdapter {
  baseLayer: TileLayer;
  isStreetView: boolean;
  attachMap(map: OlMap): void;
  setCenter(lat: number, lng: number): void;
  setZoom(level: number): void;
  pan(direction: 'up' | 'down' | 'left' | 'right'): void;
  getCenter(): { lat: number; lng: number };
  getZoom(): number;
}

export class MapAdapter implements IMapAdapter {
  baseLayer: TileLayer;
  isStreetView: boolean;
  private olMap: OlMap | null = null;

  constructor(private mapEntity: MapEntity) {
    this.baseLayer = this.createLayer(mapEntity);
    // googleMaps and openStreetMap are both street views
    this.isStreetView = 
      mapEntity.type === 'googleMaps' || 
      mapEntity.type === 'openStreetMap';
  }

  attachMap(map: OlMap) {
    this.olMap = map;
  }

  private createLayer(mapEntity: MapEntity): TileLayer {
      '';
    switch (mapEntity.type) {
      case 'googleMaps':
        return createGoogleLayer();
      case 'satelliteMap':
        return createAzureLayer();
      case 'openStreetMap':
        return createOSMLayer();
      case 'illustrated':
        return createGoogleLayer();
      
    }
  }

  setCenter(lat: number, lng: number) {
    this.olMap?.getView().setCenter(fromLonLat([lng, lat]));
  }

  setZoom(level: number) {
    this.olMap?.getView().setZoom(level);
  }

  getCenter() {
    const center = this.olMap?.getView().getCenter() ?? [0, 0];
    const [lng, lat] = toLonLat(center);
    return { lat, lng };
  }

  getZoom() {
    return this.olMap?.getView().getZoom() ?? 0;
  }

  pan(direction: 'up' | 'down' | 'left' | 'right') {
    const center = this.olMap?.getView().getCenter();
    if (!center) return;

    const zoom = this.getZoom();
    const step = this.panStep(zoom);
    const [lng, lat] = toLonLat(center);

    const next = {
      up:    { lat: lat + step, lng },
      down:  { lat: lat - step, lng },
      left:  { lat, lng: lng - step },
      right: { lat, lng: lng + step }
    }[direction];

    this.setCenter(next.lat, next.lng);
  }

  // Consolidates the repeated zoom step blocks from map-move-pad.tsx
  private panStep(zoom: number): number {
    if (zoom <= 2)  return zoom / 10;
    if (zoom <= 3)  return zoom / 20;
    if (zoom <= 5)  return zoom / 30;
    if (zoom <= 7)  return zoom / 100;
    if (zoom <= 9)  return zoom / 1024;
    if (zoom <= 11) return zoom / 2048;
    if (zoom <= 14) return zoom / 17096;
    if (zoom <= 17) return zoom / 110096;
    return zoom / 1405096;
  }
}